import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { QueryStatus, authAxios } from "../../../utils";
import { RootState } from "../../../redux/store";
import {
  ElectricVehicleApi,
  HomeChargingEligibleVehicleApi,
} from "./electricVehiclesAPI";

export interface ElectricVehiclesState {
  electricVehiclesStatus: QueryStatus;
  electricVehicles: ElectricVehicleApi[];
  homeChargingEligibleVehiclesStatus: QueryStatus;
  homeChargingEligibleVehicles: HomeChargingEligibleVehicleApi[];
}

const initialState: ElectricVehiclesState = {
  electricVehiclesStatus: "idle",
  electricVehicles: [],
  homeChargingEligibleVehiclesStatus: "idle",
  homeChargingEligibleVehicles: [],
};

export const getElectricVehiclesByCompanyAsync = createAsyncThunk(
  "getElectricVehiclesByCompanyAsync/call",
  async (_, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    if (!company) throw new Error("Company not selected");
    const axios = authAxios({ companyUuid: company.uuid });
    const response = await axios.get<ElectricVehicleApi[]>(
      `electric_charges/vehicles/company/${company.uuid}`,
    );
    return response.data;
  },
);

export const getHomeChargingEligibleVehiclesAsync = createAsyncThunk(
  "getHomeChargingEligibleVehiclesAsync/call",
  async (_, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    if (!company) throw new Error("Company not selected");
    const axios = authAxios({ companyUuid: company.uuid });
    const response = await axios.get<HomeChargingEligibleVehicleApi[]>(
      `electric_charges/vehicles/eligible/company/${company.uuid}`,
    );
    return response.data;
  },
);

export const electricVehiclesSlice = createSlice({
  name: "electricVehicles",
  initialState,
  reducers: {
    resetElectricVehiclesStatus(state) {
      state.electricVehiclesStatus = "idle";
    },
    resetHomeChargingEligibleVehiclesStatus(state) {
      state.homeChargingEligibleVehiclesStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getElectricVehiclesByCompanyAsync.pending, (state) => {
        state.electricVehiclesStatus = "processing";
      })
      .addCase(getElectricVehiclesByCompanyAsync.fulfilled, (state, action) => {
        state.electricVehiclesStatus = "success";
        state.electricVehicles = action.payload;
      })
      .addCase(getElectricVehiclesByCompanyAsync.rejected, (state) => {
        state.electricVehiclesStatus = "failed";
      })
      .addCase(getHomeChargingEligibleVehiclesAsync.pending, (state) => {
        state.homeChargingEligibleVehiclesStatus = "processing";
      })
      .addCase(
        getHomeChargingEligibleVehiclesAsync.fulfilled,
        (state, action) => {
          state.homeChargingEligibleVehiclesStatus = "success";
          state.homeChargingEligibleVehicles = action.payload;
        },
      )
      .addCase(getHomeChargingEligibleVehiclesAsync.rejected, (state) => {
        state.homeChargingEligibleVehiclesStatus = "failed";
      });
  },
});

export const selectElectricVehiclesStatus = (state: RootState) =>
  state.electricVehicles.electricVehiclesStatus;

export const selectElectricVehicles = (state: RootState) =>
  state.electricVehicles.electricVehicles;

export const selectHomeChargingEligibleVehiclesStatus = (state: RootState) =>
  state.electricVehicles.homeChargingEligibleVehiclesStatus;

export const selectHomeChargingEligibleVehicles = (state: RootState) =>
  state.electricVehicles.homeChargingEligibleVehicles;

export default electricVehiclesSlice.reducer;
